/**
 * Share a picture, and fall back to the link when the picture cannot travel.
 *
 * The whole ladder lives here, in one module, because it is one decision taken
 * in order and the steps are only meaningful as a sequence:
 *
 *   1. FILES + text through `navigator.share`, where `canShare({files})` says
 *      the browser will take them. This is the only rung that works in the
 *      places people actually post — Instagram will not take a link at all,
 *      Messages renders one as a grey rectangle.
 *   2. The LINK through `navigator.share`, which is every browser with a share
 *      sheet and no file support.
 *   3. The CLIPBOARD, which is the honest second best on desktop and on an
 *      insecure dev origin, and which the caller can tell the reader about.
 *   4. Nothing, said out loud, so the button never looks like it worked.
 *
 * Everything that can go wrong on the way (no `navigator.share`, a picture that
 * has not been rendered, a fetch that times out, a phone with no signal) falls
 * to the next rung down. Nobody loses a behaviour.
 *
 * `navigator` arrives as a defaulted parameter so this is testable and SSR-safe.
 */

export const SHARE_RESULTS = ['shared-image', 'shared', 'copied', 'cancelled', 'unavailable'];

/**
 * The file payload carries no `url`.
 *
 * iOS collapses a mixed `files` + `url` share back into a link share, which
 * would silently undo the whole feature on the one platform it matters most on.
 * The address rides inside `text` instead, so a recipient can still get back —
 * which is why `carShareMessage` ends its copy on a phrase that reads correctly
 * with an address after it.
 */
function filePayload({ title, text, url }, file) {
    return { files: [file], title, text: url ? `${text} — ${url}` : text };
}

async function shareTheFile(nav, payload, file) {
    if (typeof nav.canShare !== 'function' || !nav.canShare({ files: [file] })) {
        return null;
    }

    try {
        await nav.share(filePayload(payload, file));

        return 'shared-image';
    } catch (error) {
        // Dismissing the sheet is a decision, not a failure. Falling through to
        // the link would share something the reader just declined to send.
        if (error?.name === 'AbortError') {
            return 'cancelled';
        }

        return null;
    }
}

export async function shareLink({ title, text, url }, nav = typeof navigator === 'undefined' ? null : navigator) {
    if (!nav) {
        return 'unavailable';
    }

    if (typeof nav.share === 'function') {
        try {
            await nav.share({ title, text, url });

            return 'shared';
        } catch (error) {
            if (error?.name === 'AbortError') {
                return 'cancelled';
            }
        }
    }

    if (typeof nav.clipboard?.writeText === 'function') {
        try {
            await nav.clipboard.writeText(url);

            return 'copied';
        } catch (error) {
            return 'unavailable';
        }
    }

    return 'unavailable';
}

/**
 * `loadFile` is a thunk rather than a URL, because what a caller has to do to
 * produce its file differs — a car needs its id and its name, the app needs the
 * language on screen — and none of that belongs to the ladder.
 */
export async function shareImage(
    { title, text, url },
    loadFile,
    nav = typeof navigator === 'undefined' ? null : navigator,
) {
    if (!nav) {
        return 'unavailable';
    }

    const payload = { title, text, url };

    let file = null;

    try {
        file = await loadFile();
    } catch {
        // No picture, no network, no File constructor: all the same answer here.
        return shareLink(payload, nav);
    }

    if (typeof nav.share === 'function') {
        const result = await shareTheFile(nav, payload, file);

        if (result) {
            return result;
        }
    }

    // A browser that cannot take the file gets the link. There is deliberately
    // no silent download here: a file appearing in someone's Downloads folder
    // because the share sheet happened to be missing is a surprise, not a
    // fallback.
    return shareLink(payload, nav);
}
